import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type'
import { FormTransformers } from '@/common/utils/formTransformers'
import { updateFeesConfigurationsSchema, UpdateFeesConfigurationsValidationSchema } from '@/features/establishments/schemas/politics/updateFeesConfigurations.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { specialFeeApplicationMode, specialFeeApplicationModeOptions } from '../constants/specialFeeApplicationMode.constant'
import { useUpdateEstablishmentFeesConfigurations } from '../hooks/useUpdateEstablishmentFeesConfigurations'

interface UpdateFeesConfigurationsModalProps {
  establishmentCompany?: EstablishmentCompanyData
  isFetching?: boolean
}

export const UpdateFeesConfigurationsModal = ({ establishmentCompany, isFetching }: UpdateFeesConfigurationsModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<UpdateFeesConfigurationsValidationSchema>({
    resolver: zodResolver(updateFeesConfigurationsSchema),
  })

  const {
    mutate: updateFeesConfigurations,
    isPending: updateFeesConfigurationsIsPending
  } = useUpdateEstablishmentFeesConfigurations({
    establishmentCompany: establishmentCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] })
      setIsOpen(false)
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdateFeesConfigurationsValidationSchema> = (form) => updateFeesConfigurations(form)

  useEffect(() => {
    if (isOpen) {
      reset({
        special_fee_application_mode: {
          value: establishmentCompany?.special_fee_application_mode,
          label: establishmentCompany ? specialFeeApplicationMode[establishmentCompany?.special_fee_application_mode] : '',
        },
      })
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_fees_configurations_modal"
          operationKlassToCheckPermission="Admin::Establishment::Companies::UpdateFeesConfigurations"
          className="h-[2.125rem] text-sm text-white-800"
          disabled={!!isFetching}
        >
          Editar
        </Button>
      }
      title="Editando Configurações de tarifas"
      dataTestid="update_fees_configurations"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700 rounded-br-lg"
      cancelButtonClassName="bg-white-100 rounded-bl-lg"
      disabled={updateFeesConfigurationsIsPending}
      confirmButtonIsLoading={updateFeesConfigurationsIsPending}
      bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="special_fee_application_mode"
              label="Modo de aplicação da tarifa especial"
              errorMessage={errors?.special_fee_application_mode?.message || undefined}
              options={specialFeeApplicationModeOptions || []}
              isDisabled={updateFeesConfigurationsIsPending}
            />
          </form>

          {messages && (
            <ModalMessageSimple
              messages={messages}
              setMessages={setMessages}
              isError
            />
          )}
        </>
      }
    />
  )
}
